import { useState, useEffect, useCallback, useRef } from 'react';

/**
 * Normaliza una nota recibida del backend
 */
export const normalizeTicketNote = item => {
  if (!item) return null;
  return {
    idTicketNotas: item.idTicketNotas ?? item.IdTicketNotas ?? item.noteId ?? item.NoteId ?? item.idTicketNota ?? item.id,
    idTicket: item.idTicket ?? item.IdTicket ?? item.ticketId,
    note: item.note ?? item.Note ?? item.vNotaTicket ?? item.VNotaTicket ?? item.vNota ?? item.VNota ?? item.vContenido ?? item.texto ?? '',
    noteColor: item.noteColor ?? item.NoteColor ?? item.vColorNota ?? item.VColorNota ?? item.vColor ?? item.color ?? '#fef3c7',
    createdUser: item.createdUser ?? item.CreatedUser ?? item.vCreatedUser ?? item.VCreatedUser ?? item.vUsuario ?? item.usuario ?? 'Asesor',
    createdDate: item.createdDate ?? item.CreatedDate ?? item.dCreatedDate ?? item.DCreatedDate ?? item.dFechaCreacion ?? item.DFechaCreacion ?? item.dFecha ?? new Date().toISOString(),
    isActive: item.bIsActive ?? item.BIsActive ?? item.bActivo ?? item.BActivo ?? item.isActive ?? true
  };
};

/**
 * Hook para consultar y gestionar las notas internas de un ticket/cliente
 */
export const useTicketNotes = ({ activeTicketId, token }) => {
  const [notes, setNotes] = useState([]);
  const [isLoadingNotes, setIsLoadingNotes] = useState(false);
  const [isSavingNote, setIsSavingNote] = useState(false);
  const lastTicketIdRef = useRef(null);

  const fetchNotes = useCallback(async () => {
    if (!activeTicketId || !token) {
      setNotes([]);
      return [];
    }

    setIsLoadingNotes(true);
    try {
      const response = await fetch(`/api/SupportEngine/GetTicketNotes?idTicket=${encodeURIComponent(activeTicketId)}`, {
        headers: {
          Accept: 'application/json',
          Authorization: 'Bearer ' + token
        }
      });

      if (response.ok) {
        const result = await response.json();
        const rawList = Array.isArray(result)
          ? result
          : Array.isArray(result?.data)
            ? result.data
            : [];

        const normalized = rawList
          .map(normalizeTicketNote)
          .filter(n => n && n.idTicketNotas && n.isActive !== false);

        // Ordenar de más reciente a más antigua
        normalized.sort((a, b) => new Date(b.createdDate) - new Date(a.createdDate));

        setNotes(normalized);
        return normalized;
      } else {
        setNotes([]);
      }
    } catch (err) {
      console.error('Error al cargar notas del ticket:', err);
      setNotes([]);
    } finally {
      setIsLoadingNotes(false);
    }
    return [];
  }, [activeTicketId, token]);

  useEffect(() => {
    if (lastTicketIdRef.current === activeTicketId) return;
    lastTicketIdRef.current = activeTicketId;
    fetchNotes();
  }, [activeTicketId, fetchNotes]);

  const createNote = useCallback(async ({ note, noteColor = '#fef3c7' }) => {
    if (!activeTicketId || !note?.trim() || !token) {
      throw new Error('La nota no puede estar vacía');
    }
    console.log ("note", note)
    setIsSavingNote(true);
    try {
      const response = await fetch('/api/SupportEngine/CreateTicketNote', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json',
          Authorization: 'Bearer ' + token
        },
        body: JSON.stringify({
            ticketId: Number(activeTicketId),
          note: note.trim(),
          noteColor: noteColor || '#fef3c7'
        })
      });

      if (!response.ok) {
        const err = await response.json().catch(() => ({}));
        throw new Error(err.message || 'Error al crear la nota');
      }

      await fetchNotes();
      return true;
    } finally {
      setIsSavingNote(false);
    }
  }, [activeTicketId, token, fetchNotes]);

  const updateNote = useCallback(async ({ idTicketNotas, note, noteColor }) => {
    if (!idTicketNotas || !note?.trim() || !token) {
      throw new Error('La nota no puede estar vacía');
    }

    setIsSavingNote(true);
    try {
      const response = await fetch('/api/SupportEngine/UpdateTicketNote', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json',
          Authorization: 'Bearer ' + token
        },
        body: JSON.stringify({
          idTicketNotas: Number(idTicketNotas),
          idTicket: Number(activeTicketId),
          note: note.trim(),
          noteColor: noteColor || '#fef3c7'
        })
      });

      if (!response.ok) {
        const err = await response.json().catch(() => ({}));
        throw new Error(err.message || 'Error al actualizar la nota');
      }

      await fetchNotes();
      return true;
    } finally {
      setIsSavingNote(false);
    }
  }, [activeTicketId, token, fetchNotes]);

  const deleteNote = useCallback(async ({ idTicketNotas }) => {
    if (!idTicketNotas || !token) {
      throw new Error('IdTicketNotas es requerido');
    }

    setIsSavingNote(true);
    try {
      const response = await fetch('/api/SupportEngine/DeleteTicketNote', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json',
          Authorization: 'Bearer ' + token
        },
        body: JSON.stringify({
          idTicketNotas: Number(idTicketNotas),
          idTicket: Number(activeTicketId)
        })
      });

      if (!response.ok) {
        const err = await response.json().catch(() => ({}));
        throw new Error(err.message || 'Error al eliminar la nota');
      }

      await fetchNotes();
      return true;
    } finally {
      setIsSavingNote(false);
    }
  }, [activeTicketId, token, fetchNotes]);

  return {
    notes,
    notesCount: notes.length,
    isLoadingNotes,
    isSavingNote,
    fetchNotes,
    createNote,
    updateNote,
    deleteNote
  };
};
